<template>
    <div>
      <!-- 広告本体（コンポーネントに渡された class/style はここへ転送） -->
      <ins
        ref="root"
        class="adsbygoogle"
        :class="$attrs.class"
        :style="$attrs.style || defaultStyle"
        :data-ad-client="normClient"
        :data-ad-slot="normSlot"
        :data-ad-format="normFormat"
        :data-full-width-responsive="normFullResp"
      />
      <!-- ノーフィル時に出す任意のフォールバック -->
      <slot name="fallback" v-if="empty" />
    </div>
  </template>
  
  <script>
  export default {
    name: 'Adsense',
    inheritAttrs: false, // class/style を root(div)ではなく <ins> に渡すため
    props: {
      // 正式 data-* 系（どちらかを使えばOK）
      dataAdClient: { type: String, default: null },
      dataAdSlot:   { type: [String, Number], default: null },
      dataAdFormat: { type: String, default: null },
      dataFullWidthResponsive: { type: [String, Boolean], default: null },
  
      // 互換エイリアス（以前の ad-* / fullWidthResponsive を許容）
      adClient: { type: String, default: null },
      adSlot:   { type: [String, Number], default: null },
      adFormat: { type: String, default: null },
      fullWidthResponsive: { type: [String, Boolean], default: null },
  
      // ノーフィル時の挙動
      collapseIfNoFill: { type: Boolean, default: false },
      collapseAfter:    { type: Number,  default: 3000 }
    },
    data() {
      return {
        empty: false,
        io: null,        // IntersectionObserver
        mo: null         // MutationObserver
      }
    },
    computed: {
      // デフォルトは publisher ID をハードコード
      normClient() {
        return this.dataAdClient || this.adClient || 'ca-pub-3076785709839281'
      },
      normSlot() {
        const v = this.dataAdSlot != null ? this.dataAdSlot : this.adSlot
        return v != null ? String(v) : ''
      },
      normFormat() {
        return this.dataAdFormat || this.adFormat || 'auto'
      },
      normFullResp() {
        const v = this.dataFullWidthResponsive != null ? this.dataFullWidthResponsive : this.fullWidthResponsive
        // truthy/falsy を文字列に正規化
        return String(v == null ? true : v)
      },
      defaultStyle() {
        // ※ ここは “コンポーネントの style prop” ではなく <ins> のデフォルト style
        return 'display:block; text-align:center; min-height:1px; min-width:1px;'
      }
    },
    mounted() {
      this.requestAdLazily()
      // ルート遷移の度に再リクエスト
      this.unwatchRoute = this.$watch(
        () => this.$route.fullPath,
        () => this.requestAdLazily()
      )
    },
    beforeDestroy() {
      if (this.io) this.io.disconnect()
      if (this.mo) this.mo.disconnect()
      if (this.unwatchRoute) this.unwatchRoute()
    },
    methods: {
      collapse() {
        if (!this.collapseIfNoFill) return
        const el = this.$refs.root
        if (!el) return
        el.style.display = 'none'
        this.empty = true
      },
      scheduleCollapseCheck(el) {
        // 既に iframe があれば表示維持
        if (el.querySelector('iframe')) {
          this.empty = false
          el.style.display = 'block'
          return
        }
        // iframe 生成を監視
        this.mo = new MutationObserver(() => {
          if (el.querySelector('iframe')) {
            this.empty = false
            el.style.display = 'block'
            this.mo && this.mo.disconnect()
            this.mo = null
          }
        })
        this.mo.observe(el, { childList: true, subtree: true })
  
        // タイムアウトで畳む（ノーフィル想定）
        const timeout = this.collapseAfter || 3000
        setTimeout(() => {
          this.mo && this.mo.disconnect()
          this.mo = null
          if (!el.querySelector('iframe')) this.collapse()
        }, timeout)
      },
      requestAd() {
        const el = this.$refs.root
        if (!el) return
        el.style.display = 'block'
        this.empty = false
  
        const tick = (retry = 0) => {
          const w = window
          const q = w.adsbygoogle
          if (q && Array.isArray(q)) {
            try { q.push({}) }
             catch(e) {
                 // noop: ignore if adsbygoogle not loaded
             }
            this.scheduleCollapseCheck(el)
          } else if (retry < 20) {
            setTimeout(() => tick(retry + 1), 100) // 最大2秒待機
          } else {
            // AdBlock 等でスクリプト未ロード
            this.scheduleCollapseCheck(el)
          }
        }
        tick()
      },
      requestAdLazily() {
        const el = this.$refs.root
        if (!el) return
  
        // すでにあれば再利用
        if (this.io) this.io.disconnect()
        this.io = new IntersectionObserver((entries) => {
          if (entries.some(e => e.isIntersecting)) {
            this.io && this.io.disconnect()
            this.io = null
            this.requestAd()
          }
        }, { rootMargin: '200px 0px' })
        this.io.observe(el)
      }
    }
  }
  </script>